import React, { createContext, useContext, useMemo } from 'react';
import { buildPeopleIndex, groupSeries } from '../lib/catalog';

/**
 * Contexte catalogue.
 *
 * `App` reste propriétaire de l'état (films, liste, progression) ; ce contexte
 * se contente de le dériver une seule fois et de le mettre à disposition des
 * pages. Sans lui, chaque nouvel écran devrait traverser `AppRoutes` en
 * cascade de props.
 *
 * Les deux dérivations coûteuses — regroupement des séries et index des
 * personnes — sont mémorisées sur la seule référence `movies`. Elles tournent
 * donc une fois au chargement, pas à chaque rendu comme le faisait l'ancien
 * calcul en ligne dans `App`.
 */
const CatalogContext = createContext(null);

export function CatalogProvider({
  movies,
  watchlist,
  watchedMovies,
  watchProgress,
  progressList,
  dislikedMovies,
  onOpenTitle,
  onToggleWatchlist,
  onForgetProgress,
  currentUser,
  children,
}) {
  const dislikedSet = useMemo(
    () => new Set((dislikedMovies || []).map(String)),
    [dislikedMovies]
  );

  // Les titres écartés d'un revers de main ne remontent plus dans les
  // sélections éditoriales — ils restent atteignables par la recherche.
  const titles = useMemo(
    () => groupSeries(movies).filter((t) => !dislikedSet.has(String(t.id))),
    [movies, dislikedSet]
  );
  const people = useMemo(() => buildPeopleIndex(titles), [titles]);

  // Sets : la recherche d'appartenance passe de O(n) à O(1). Sur 2 300 titres
  // et une grille de 30 affiches, cela retire plusieurs milliers de parcours
  // de tableau par rendu.
  const listSet = useMemo(() => new Set((watchlist || []).map(String)), [watchlist]);
  const watchedSet = useMemo(() => new Set((watchedMovies || []).map(String)), [watchedMovies]);

  const value = useMemo(
    () => ({
      movies: movies || [],
      titles,
      people,
      currentUser,
      onOpenTitle,
      onToggleWatchlist,
      onForgetProgress,
      watchProgress: watchProgress || {},
      progressList: progressList || [],
      isDisliked: (title) => dislikedSet.has(String(title.id)),

      /** Un titre est « dans ma liste » si lui — ou l'un de ses épisodes — y est. */
      isInList: (title) =>
        title.isGroupedSeries
          ? (title.episodes || []).some((ep) => listSet.has(String(ep.id)))
          : listSet.has(String(title.id)),

      /** Une série est « vue » dès qu'un épisode l'est (marqueur de reprise). */
      isWatched: (title) =>
        title.isGroupedSeries
          ? (title.episodes || []).some((ep) => watchedSet.has(String(ep.id)))
          : watchedSet.has(String(title.id)),

      progressOf: (title) => (watchProgress || {})[String(title.id)] || 0,
    }),
    [movies, titles, people, currentUser, onOpenTitle, onToggleWatchlist, onForgetProgress, watchProgress, progressList, listSet, watchedSet, dislikedSet]
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

/** Accès au catalogue. Retourne `null` hors du provider (écrans admin isolés). */
export function useCatalog() {
  return useContext(CatalogContext);
}

export default CatalogContext;
